import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import getSitePingLatest, {
  PingLatestResponse,
} from "../http/getSitePingLatest";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

/**
 * Most recent Soracom downlink ping result for the given site.
 *
 * Drives the "Site internet connection" tile headline on
 * `/site/<id>/health/status`. The single-row shape is intentional — the
 * chart-series version goes through `useChartData({dataType:"ping_history"})`.
 *
 * Polls every 60 seconds while the page is open so the operator sees
 * the next 15-min ping check land without manually refreshing.
 */
const useSitePingLatest = (
  siteId: string,
  enabled = true
): UseQueryResult<PingLatestResponse, unknown> => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["site", siteId, "ping-latest"],
    async () =>
      getSitePingLatest(await getAuthTokenBlocking(getToken, relogin), siteId),
    {
      enabled: enabled && !!siteId,
      // Ping check runs every 15 min; refetch every minute while the
      // page is open so a fresh result appears soon after it lands.
      refetchInterval: 60_000,
      staleTime: 30_000,
    }
  );
};

export default useSitePingLatest;
